/**
 * A start list from a timing company, read in the browser: CSV (comma, semicolon or tab), its columns guessed from
 * their headers and confirmed by the host, each row turned into an athlete for the batch endpoint. Races are matched
 * to the event's courses by key, name or distance ("42", "42km", "Full"), since start lists name them their own way.
 */

import { courseKm } from '@/components/map/course-map/courses'
import type { AthleteFields, Gender, Race } from './types'

export const IMPORT_FIELDS = ['name', 'first_name', 'last_name', 'bib', 'gender', 'race', 'tracker', 'external_ref'] as const
export type ImportField = (typeof IMPORT_FIELDS)[number]
export type ColumnMapping = Partial<Record<ImportField, number>>

export const FIELD_LABELS: Record<ImportField, string> = {
  name: 'Full name',
  first_name: 'First name',
  last_name: 'Last name',
  bib: 'Bib',
  gender: 'Gender',
  race: 'Race',
  tracker: 'Tracker id',
  external_ref: 'Timing id',
}

const HEADER_HINTS: Record<ImportField, RegExp> = {
  name: /^(full ?name|name|athlete|athlete ?name|runner|participant)$/,
  first_name: /^(first ?name|given ?name|first|forename)$/,
  last_name: /^(last ?name|surname|family ?name|last)$/,
  bib: /^(bib|bib ?(no|nr|number|#)|race ?(no|number)|number|no)$/,
  gender: /^(gender|sex|m ?\/ ?f)$/,
  race: /^(race|distance|course|race ?name|event)$/,
  tracker: /^(tracker|tracker ?(id|ref|no|number)|device|device ?id|imei|lnx|lnx ?id)$/,
  external_ref: /^(id|external ?(id|ref)|athlete ?id|participant ?id|entry ?id|timing ?id|reg(istration)? ?id)$/,
}

/** A file's text, read with FileReader, which every browser has (Blob.text arrived in Safari only in 14). */
export function readFileText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

/** Rows of cells. Quoted cells may hold delimiters, quotes ("") and line breaks; a BOM and blank lines are dropped. */
export function parseCsv(text: string): string[][] {
  const source = text.replace(/^﻿/, '')
  const delimiter = detectDelimiter(source)
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < source.length; i++) {
    const char = source[i]
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') {
        cell += '"'
        i++
      } else if (char === '"') {
        quoted = false
      } else {
        cell += char
      }
    } else if (char === '"' && cell === '') {
      quoted = true
    } else if (char === delimiter) {
      row.push(cell)
      cell = ''
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else {
      cell += char
    }
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell)
    rows.push(row)
  }
  return rows.map((cells) => cells.map((value) => value.trim())).filter((cells) => cells.some((value) => value !== ''))
}

/** The delimiter of the first line, outside quotes: the most frequent of comma, semicolon and tab. */
function detectDelimiter(source: string): string {
  const firstLine = source.split(/\r?\n/, 1)[0].replace(/"[^"]*"/g, '')
  const counts = [',', ';', '\t'].map((delimiter) => ({ delimiter, count: firstLine.split(delimiter).length - 1 }))
  return counts.reduce((best, each) => (each.count > best.count ? each : best)).delimiter
}

const normalise = (value: string) => value.toLowerCase().replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim()

/** Each field's column, guessed from the header row; a column serves one field at most. */
export function guessMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {}
  const taken = new Set<number>()
  for (const field of IMPORT_FIELDS) {
    const column = headers.findIndex((header, index) => !taken.has(index) && HEADER_HINTS[field].test(normalise(header)))
    if (column >= 0) {
      mapping[field] = column
      taken.add(column)
    }
  }
  // A full name wins over first and last only when there is no pair to build it from.
  if (mapping.name !== undefined && mapping.first_name !== undefined && mapping.last_name !== undefined) delete mapping.name
  return mapping
}

export function readGender(value: string): Gender | null | undefined {
  const text = normalise(value)
  if (!text) return null
  if (['f', 'w', 'female', 'woman', 'women', 'ladies', 'lady'].includes(text)) return 'female'
  if (['m', 'male', 'man', 'men'].includes(text)) return 'male'
  return undefined
}

/**
 * The course a start list's race names: its key or name, a distance ("42", "42km", "21.1 km") within a kilometre of
 * one course's, or a word only one course's name has ("full", "strike"). Null when none or several fit.
 */
export function matchRace(value: string, races: readonly Race[]): Race | null {
  const text = normalise(value)
  if (!text) return null
  const exact = races.find((race) => normalise(race.route_key) === text || normalise(race.name) === text)
  if (exact) return exact
  const number = text.match(/(\d+(?:[.,]\d+)?)/)
  if (number) {
    const km = Number(number[1].replace(',', '.')) * (/\bmi(les?)?\b/.test(text) ? 1.609344 : 1)
    const near = races.filter((race) => {
      const length = courseKm(race)
      return length !== null && Math.abs(length - km) <= 1
    })
    if (near.length === 1) return near[0]
  }
  const words = races.filter((race) => normalise(race.name).split(' ').includes(text))
  return words.length === 1 ? words[0] : null
}

export interface ImportRow {
  /** Its row, counting the header as row 1 (blank rows aren't counted), as a spreadsheet shows it. */
  line: number
  fields: AthleteFields
  /** What the import leaves out of this row, or why it can't be imported. */
  notes: string[]
  /** False when the row can't be imported at all (no name). */
  importable: boolean
}

const cellOf = (row: string[], column: number | undefined) => (column === undefined ? '' : (row[column] ?? '').trim())

/** The file's data rows as athletes. Only mapped columns are sent, so an import never clears what it doesn't carry. */
export function importRows(rows: string[][], mapping: ColumnMapping, races: readonly Race[]): ImportRow[] {
  return rows.slice(1).map((row, index) => {
    const notes: string[] = []
    const fields: AthleteFields = {}
    const name = cellOf(row, mapping.name) || [cellOf(row, mapping.first_name), cellOf(row, mapping.last_name)].filter(Boolean).join(' ')
    if (name) fields.name = name
    const bib = cellOf(row, mapping.bib)
    if (bib) fields.bib = bib
    const externalRef = cellOf(row, mapping.external_ref)
    if (externalRef) fields.external_ref = externalRef
    if (mapping.gender !== undefined) {
      const gender = readGender(cellOf(row, mapping.gender))
      if (gender === undefined) notes.push(`Gender “${cellOf(row, mapping.gender)}” isn’t F or M, so it’s left out`)
      else if (gender) fields.gender = gender
    }
    if (mapping.race !== undefined) {
      const value = cellOf(row, mapping.race)
      const race = matchRace(value, races)
      if (race) fields.route_id = race.id
      else if (value) notes.push(`Race “${value}” isn’t one of your courses, so it’s left out`)
    }
    const tracker = cellOf(row, mapping.tracker)
    if (tracker) fields.tracker_ref = tracker
    const importable = Boolean(name || bib || externalRef)
    if (!name && importable) notes.push('No name: it only updates an athlete already on the roster')
    if (!importable) notes.push('No name, bib or timing id')
    return { line: index + 2, fields, notes, importable }
  })
}
